<script setup>
import { ref, reactive, computed, onMounted, onActivated, watch } from 'vue'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import { usePaginacao } from '@/composables/usePaginacao'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import CampoBusca from '@/components/base/campos/CampoBusca.vue'
import CampoDataIntervalo from '@/components/base/campos/CampoDataIntervalo.vue'
import ConfirmationModal from '@/components/base/modal/ConfirmationModal.vue'
import SolicitacaoAdiantamentoModal from '@/components/Financeiro/contasPagarReceber/solicitacaoAdiantamento/SolicitacaoAdiantamentoModal.vue'
import { parceiroSearch } from '@/search-definitions/parceiroSearch'
import { formatarDataBR } from '@/utils/useDataBR'
import { FIN_ST_SOL_ADT } from '@/constants/FIN_ST_SOL_ADT'

const emit = defineEmits(['adicionar-item', 'editar-item', 'show-notification'])

const authStore = useAuthStore()
const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao(25)

const lista = ref([])
const loading = ref(false)
const erro = ref(null)
const filtrosAbertos = ref(false)

const sortField = ref('')
const sortOrder = ref('')

const colunas = [
  { field: 'idSolicitacaoAdiantamento', label: 'ID', width: 80, sortable: true, slotName: 'id' },
  { field: 'nomeParceiro', label: 'Fornecedor', sortable: true, class: 'fw-medium text-body-emphasis' },
  { field: 'valorTotal', label: 'Valor Total', align: 'right', sortable: true, formatter: (v) => formatarValor(v) },
  { field: 'nomeMoeda', label: 'Moeda', align: 'center', sortable: true },
  { field: 'dataVencimento', label: 'Vencimento', align: 'center', sortable: true, slotName: 'dataVencimento' },
  { field: 'nomeStatusSolicitacao', label: 'Status', align: 'center', slotName: 'status', sortable: true },
  { field: 'acoes', label: 'Ações', width: 160, align: 'right', slotName: 'acoes', resizable: false, sortable: false }
]

const listas = reactive({
  statusSolicitacao: []
})

const filtros = reactive({
  buscaLivre: '',
  lstStatusSolicitacao: FIN_ST_SOL_ADT.Pendente,
  idParceiro: null,
  nomeParceiro: '',
  idEmbarque: null,
  dataInicio: '',
  dataFim: ''
})

const configBuscaParceiro = computed(() => ({
  ...parceiroSearch,
  titulo: 'Buscar Fornecedor',
  filtrosFixos: { idEmpresa: authStore.empresaAtiva, isFornecedor: true }
}))

const temPermissaoCompleta = computed(() => authStore.isAdminInterno || (authStore.permissoes && authStore.permissoes.includes('MODULO_COMPLETO')))

watch(() => authStore.empresaAtiva, async (novaEmpresa) => {
  if (novaEmpresa) {
    await carregarListas()
    buscarDados(1)
  } else {
    lista.value = []
    paginacao.value.TotalCount = 0
    listas.statusSolicitacao = []
  }
})

const carregarListas = async () => {
  try {
    const resStatus = await api.get('/ListasSistema/lookup/FIN_ST_SOL_ADT')
    listas.statusSolicitacao = resStatus.data
  } catch (e) {
    console.warn('Erro ao carregar listas auxiliares:', e)
  }
}

const buscarDados = async (pageNumber = 1) => {
  if (authStore.isAdminInterno && !authStore.empresaAtiva) {
    erro.value = 'Por favor, selecione uma Empresa no topo da página.'
    lista.value = []
    return
  }

  loading.value = true
  erro.value = null

  try {
    const params = injetarParametros(pageNumber)

    if (authStore.empresaAtiva) params.append('idEmpresa', authStore.empresaAtiva)
    if (sortField.value) params.append('sortField', sortField.value)
    if (sortOrder.value) params.append('sortOrder', sortOrder.value)
    if (filtros.buscaLivre) params.append('buscaLivre', filtros.buscaLivre)
    if (filtros.lstStatusSolicitacao) params.append('lstStatusSolicitacao', filtros.lstStatusSolicitacao)
    if (filtros.idParceiro) params.append('idParceiro', filtros.idParceiro)
    if (filtros.dataInicio) params.append('dataInicio', filtros.dataInicio)
    if (filtros.dataFim) params.append('dataFim', filtros.dataFim)

    const response = await api.get(`/SolicitacoesAdiantamento?${params.toString()}`)
    lista.value = response.data

    atualizarPaginacao(response.headers)
  } catch (error) {
    erro.value = 'Não foi possível carregar as solicitações de adiantamento.'
    lista.value = []
    paginacao.value.TotalCount = 0
  } finally {
    loading.value = false
  }
}

const aplicarFiltros = () => { buscarDados(1) }

const limparFiltros = () => {
  filtros.buscaLivre = ''
  filtros.lstStatusSolicitacao = ''
  filtros.idParceiro = null
  filtros.nomeParceiro = ''
  filtros.dataInicio = ''
  filtros.dataFim = ''
  buscarDados(1)
}

const mudarPagina = (page) => {
  if (page >= 1 && page <= (paginacao.value.TotalPages || 1)) {
    buscarDados(page)
  }
}

const mudarTamanhoPagina = (novoTamanho) => {
  paginacao.value.PageSize = novoTamanho
  buscarDados(1)
}

// --- VISUALIZAÇÃO VIA MODAL ---
const showModalVisualizar = ref(false)
const modalVisualizarData = ref(null)

const visualizarRegistro = async (item) => {
  try {
    loading.value = true
    const response = await api.get(`/SolicitacoesAdiantamento/${item.idSolicitacaoAdiantamento}`)
    modalVisualizarData.value = response.data
    showModalVisualizar.value = true
  } catch (error) {
    emit('show-notification', { message: 'Erro ao carregar dados da solicitação.', type: 'error' })
  } finally {
    loading.value = false
  }
}

const criarTitulo = async (item) => {
  try {
    loading.value = true
    const response = await api.get(`/SolicitacoesAdiantamento/${item.idSolicitacaoAdiantamento}`)
    // Emite com flag indicando que é para criar título a partir da solicitação
    emit('editar-item', { ...response.data, _criarTitulo: true })
  } catch (error) {
    emit('show-notification', { message: 'Erro ao carregar dados da solicitação.', type: 'error' })
  } finally {
    loading.value = false
  }
}

// --- CANCELAMENTO COM JUSTIFICATIVA ---
const confirmationModalRef = ref(null)
const solicitacaoParaCancelar = ref(null)
const justificativaCancelamento = ref('')
const isCancelando = ref(false)

const confirmarCancelamento = (item) => {
  solicitacaoParaCancelar.value = item
  justificativaCancelamento.value = ''
  confirmationModalRef.value?.show()
}

const onConfirmarCancelamento = async (confirmou) => {
  if (!confirmou || !solicitacaoParaCancelar.value) {
    solicitacaoParaCancelar.value = null
    justificativaCancelamento.value = ''
    return
  }

  if (!justificativaCancelamento.value || justificativaCancelamento.value.trim() === '') {
    emit('show-notification', { message: 'A justificativa é obrigatória para cancelamento.', type: 'warning' })
    return
  }

  isCancelando.value = true
  try {
    await api.put(`/SolicitacoesAdiantamento/${solicitacaoParaCancelar.value.idSolicitacaoAdiantamento}/cancelar`, {
      justificativa: justificativaCancelamento.value.trim()
    })
    emit('show-notification', { message: 'Solicitação cancelada com sucesso!', type: 'success' })
    solicitacaoParaCancelar.value = null
    justificativaCancelamento.value = ''
    buscarDados(paginacao.value.CurrentPage || 1)
  } catch (error) {
    const errorMsg = error.response?.data?.message || 'Erro ao cancelar solicitação.'
    emit('show-notification', { message: errorMsg, type: 'error' })
  } finally {
    isCancelando.value = false
  }
}

const formatarValor = (valor) => {
  if (valor == null) return '-'
  try {
    return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(valor)
  } catch (e) {
    return valor?.toFixed(2) ?? '-'
  }
}

const obterCorStatus = (lstStatus) => {
  if (lstStatus?.includes('FIN_ST_SOL_ADT1')) return 'badge-ghost-warning'
  if (lstStatus?.includes('FIN_ST_SOL_ADT2')) return 'badge-ghost-success'
  if (lstStatus?.includes('FIN_ST_SOL_ADT3')) return 'badge-ghost-danger'
  return 'badge-ghost-secondary'
}

onMounted(async () => {
  if (authStore.empresaAtiva || !authStore.isAdminInterno) {
    await carregarListas()
    buscarDados(1)
  }
})

onActivated(() => {
  buscarDados(paginacao.value.CurrentPage || 1)
})

defineExpose({ buscarDados })
</script>

<template>
  <PaginaAdministrar
    titulo="Solicitações de Adiantamento"
    subtitulo="Requisições de Adiantamento Financeiro (ADT)"
    v-model:filtros-abertos="filtrosAbertos"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    :mostrar-botao-adicionar="temPermissaoCompleta"
    label-botao-adicionar="Nova Solicitação"
    @mudar-pagina="mudarPagina"
    @mudar-tamanho-pagina="mudarTamanhoPagina"
    @adicionar="emit('adicionar-item')"
    @atualizar="aplicarFiltros"
  >
    <template #acoes="{ toggleFiltros }">
      <button class="btn btn-dark" @click="toggleFiltros">
        <i class="bi bi-filter-lg me-2"></i> Filtros
      </button>
    </template>

    <template #filtros>
      <div class="row g-3 align-items-end">
        <div class="col-md-4">
          <label class="form-label">Busca Livre (Obs / Doc)</label>
          <input type="text" class="form-control" v-model="filtros.buscaLivre" placeholder="Buscar..." @keydown.enter.prevent="aplicarFiltros">
        </div>
        <div class="col-md-3">
          <label class="form-label">Status</label>
          <select class="form-select" v-model="filtros.lstStatusSolicitacao" @change="aplicarFiltros">
            <option value="">Todos</option>
            <option v-for="s in listas.statusSolicitacao" :key="s.idListaSistemaItem" :value="s.idListaSistemaItem">
              {{ s.textoLista }}
            </option>
          </select>
        </div>
        <div class="col-md-5">
          <CampoBusca id="filtroParceiro" label="Fornecedor" v-model="filtros.idParceiro" :configuracao-busca="configBuscaParceiro" @selecionado="val => filtros.nomeParceiro = val.nome" />
        </div>
        <div class="col-md-3">
          <CampoDataIntervalo id="filtroData" label="Período da Solicitação" v-model:modelValueInicio="filtros.dataInicio" v-model:modelValueFim="filtros.dataFim" />
        </div>
        <div class="col-md-3 ms-auto text-end">
          <button class="btn btn-secondary me-2" @click="limparFiltros">Limpar</button>
          <button class="btn btn-primary" @click="aplicarFiltros">Aplicar Filtros</button>
        </div>
      </div>
    </template>

    <div v-if="erro" class="alert alert-danger m-3 border-0 d-flex align-items-center">
      <i class="bi bi-exclamation-triangle-fill fs-4 me-3"></i> {{ erro }}
    </div>

    <AdminTable
      v-else
      :columns="colunas"
      :data="lista"
      :loading="loading"
      empty-message="Nenhuma solicitação de adiantamento encontrada."
      row-key="idSolicitacaoAdiantamento"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      @sort-change="buscarDados(1)"
    >
      <template #id="{ row }">
        <span class="text-muted fw-medium">#{{ row.idSolicitacaoAdiantamento }}</span>
      </template>

      <template #dataVencimento="{ row }">
        <span>{{ formatarDataBR(row.dataVencimento) }}</span>
      </template>

      <template #status="{ row }">
        <span class="badge" :class="obterCorStatus(row.lstStatusSolicitacao)">
          {{ row.nomeStatusSolicitacao || row.lstStatusSolicitacao }}
        </span>
      </template>

      <template #acoes="{ row }">
        <div class="d-flex justify-content-end gap-1 acoes-wrapper">
          <button
            v-if="row.lstStatusSolicitacao === FIN_ST_SOL_ADT.Pendente && temPermissaoCompleta"
            class="btn-icon text-success"
            @click="criarTitulo(row)"
            title="Criar Título ADT"
          >
            <i class="bi bi-rocket-takeoff-fill"></i>
          </button>
          <button
            class="btn-icon"
            @click="visualizarRegistro(row)"
            title="Visualizar"
          >
            <i class="bi bi-eye-fill text-secondary"></i>
          </button>
          <button
            v-if="row.lstStatusSolicitacao === FIN_ST_SOL_ADT.Pendente && temPermissaoCompleta"
            class="btn-icon text-danger"
            @click="confirmarCancelamento(row)"
            title="Cancelar Solicitação"
          >
            <i class="bi bi-x-circle-fill"></i>
          </button>
        </div>
      </template>
    </AdminTable>

    <!-- Modal de Cancelamento com Justificativa -->
    <ConfirmationModal
      ref="confirmationModalRef"
      title="Cancelar Solicitação de Adiantamento"
      :message="`Deseja realmente cancelar a solicitação #${solicitacaoParaCancelar?.idSolicitacaoAdiantamento || ''}?`"
      confirm-button-text="Sim, cancelar"
      cancel-button-text="Não"
      @confirmed="onConfirmarCancelamento"
    >
      <template #extra>
        <div class="mt-3" v-if="solicitacaoParaCancelar">
          <label class="form-label fw-semibold">Justificativa <span class="text-danger">*</span></label>
          <textarea
            class="form-control"
            v-model="justificativaCancelamento"
            rows="3"
            placeholder="Informe o motivo do cancelamento..."
            :disabled="isCancelando"
          ></textarea>
        </div>
      </template>
    </ConfirmationModal>

    <!-- Modal de Visualização -->
    <SolicitacaoAdiantamentoModal
      v-model="showModalVisualizar"
      modo="visualizacao"
      :solicitacao="modalVisualizarData"
      :id-organizacao="authStore.empresaAtiva"
      @show-notification="(p) => emit('show-notification', p)"
    />
  </PaginaAdministrar>
</template>
